/**
 * MYAC-1337 — the three questions the Pilotage chat asks about the person, and the
 * shape their answers take on `users/{uid}.onboarding_answers`.
 *
 * The Firestore rule on `users/{uid}` pins this shape (keys, text length, a
 * server-stamped `answered_at`), and core_models_services mirrors it as
 * `OnboardingAnswers` for the reader, MYAC-1341. A skipped question is absent; the
 * whole block is absent until the first answer and again after an erase.
 */

export type OnboardingQuestion = 'role' | 'priority' | 'constraint'

/** Asked in this order, never more than these three. */
export const ONBOARDING_QUESTIONS: readonly OnboardingQuestion[] = ['role', 'priority', 'constraint']

/**
 * The quick replies of each question, as stable keys. The label shown is i18n
 * (`pilotageOpening.onboarding.<question>.replies.<key>`); the key is what the
 * backend reads, so renaming one is a schema change (CMS `Onboarding*Choice`).
 */
export const ONBOARDING_CHOICES: Record<OnboardingQuestion, readonly string[]> = {
    // MYAC-1522: the role replies say how familiar the person is with regulatory
    // work (what the chat calibrates on), not a job title they must fit into. Keys
    // added since MYAC-1337 read as their label in the chat until the chatbot
    // learns them (person_grounding falls back to the text).
    role: ['raqa_manager', 'new_to_regulatory', 'consultant', 'auditor', 'leadership'],
    priority: ['audit_preparation', 'technical_file', 'first_certification', 'keep_qms_running'],
    constraint: ['audit_date', 'short_staffed', 'where_to_start', 'scattered_documents'],
}

/**
 * MYAC-1522 — « Faisons connaissance »: the person's name first, then the three
 * questions. Only the steps still missing are asked (a person who answered the
 * questions long ago is asked their name alone), wherever they land first: the
 * dashboard's chat or a project's Pilotage chat.
 */
export type OnboardingStep = 'name' | OnboardingQuestion

export const ONBOARDING_STEPS: readonly OnboardingStep[] = ['name', ...ONBOARDING_QUESTIONS]

/** Mirrors the rule's bound on `first_name` / `last_name`. */
export const PERSONAL_NAME_MAX = 80

/**
 * The steps skipped for good (« Passer cette question »), on
 * `users/{uid}.onboarding_skipped`: a list apart from the answers, whose readers
 * expect a text. Unknown entries are dropped.
 */
export function parseOnboardingSkipped(raw: unknown): OnboardingStep[] {
    if (!Array.isArray(raw)) return []
    return ONBOARDING_STEPS.filter(step => raw.includes(step))
}

/** Mirrors the rule's bound on `text`. */
export const ONBOARDING_TEXT_MAX = 500

export interface OnboardingAnswer {
    /** What the person wrote, or the label of the reply they picked. */
    text: string
    /** The picked reply's key; null for free text. */
    choice: string | null
    answeredAt: Date | null
}

export type OnboardingAnswers = Partial<Record<OnboardingQuestion, OnboardingAnswer>>

function toDate(raw: unknown): Date | null {
    if (raw instanceof Date) return raw
    const maybe = raw as { toDate?: () => Date } | null | undefined
    return typeof maybe?.toDate === 'function' ? maybe.toDate() : null
}

/**
 * Tolerant read of the stored block: anything malformed is dropped rather than
 * shown, and no usable answer at all reads as null — the "never answered" state.
 */
export function parseOnboardingAnswers(raw: unknown): OnboardingAnswers | null {
    if (!raw || typeof raw !== 'object') return null
    const out: OnboardingAnswers = {}
    for (const question of ONBOARDING_QUESTIONS) {
        const entry = (raw as Record<string, unknown>)[question] as Record<string, unknown> | undefined
        if (!entry || typeof entry !== 'object') continue
        if (typeof entry.text !== 'string' || !entry.text.trim()) continue
        out[question] = {
            text: entry.text,
            choice: typeof entry.choice === 'string' ? entry.choice : null,
            answeredAt: toDate(entry.answered_at),
        }
    }
    return Object.keys(out).length ? out : null
}

/**
 * "Someone who skips is not asked again in the same session" — remembered per
 * browser tab session and per account. Storage can be unavailable (private mode,
 * blocked site data): then the in-memory flag in the store is the only memory.
 */
const SKIP_KEY_PREFIX = 'mac:onboarding-skipped:'

export function readSessionSkip(uid: string): boolean {
    try {
        return window.sessionStorage.getItem(SKIP_KEY_PREFIX + uid) === '1'
    } catch {
        return false
    }
}

export function writeSessionSkip(uid: string): void {
    try {
        window.sessionStorage.setItem(SKIP_KEY_PREFIX + uid, '1')
    } catch {
        // No storage: the store's in-memory flag still holds for this page's life.
    }
}

export function clearSessionSkip(uid: string): void {
    try {
        window.sessionStorage.removeItem(SKIP_KEY_PREFIX + uid)
    } catch {
        // Nothing to clear.
    }
}
